import { useState, useEffect, useRef, useCallback } from 'react';
import api from '../services/api';
import {
  LuArrowLeft, LuClock, LuSend, LuCheckCircle2, LuXCircle, LuCloud, LuUploadCloud, LuAlertTriangle,
} from 'react-icons/lu';
import { QUESTION_TYPES, errorMessage, fmtClock, fmtScore } from './trainingUtils';

/**
 * Tentativa de avaliação do colaborador.
 * - Em andamento: questões SEM gabarito (o backend nunca envia), respostas
 *   salvas automaticamente e cronômetro. O cronômetro aqui é só visual: o
 *   prazo que vale é o do servidor (deadline_at). Ao zerar, a tela envia; se
 *   o envio chegar atrasado, o servidor corrige com o que estava salvo.
 * - Encerrada: nota, resultado e — se a avaliação permitir — gabarito com
 *   explicações.
 */

const AUTOSAVE_DELAY_MS = 1200;

function ResultView({ attempt, onClose }) {
  return (
    <div className="page-card">
      <button type="button" className="training-btn training-btn--sm" onClick={onClose} style={{ marginBottom: 14 }}>
        <LuArrowLeft /> Voltar ao curso
      </button>

      <div className={`training-result-banner ${attempt.passed ? 'is-approved' : 'is-failed'}`}>
        {attempt.passed ? <LuCheckCircle2 size={34} /> : <LuXCircle size={34} />}
        <div style={{ flex: 1 }}>
          <strong style={{ fontSize: 17 }}>{attempt.passed ? 'Aprovado nesta avaliação!' : 'Não aprovado nesta tentativa'}</strong>
          <div>
            Tentativa {attempt.attempt_number} · enviada em {new Date(attempt.submitted_at).toLocaleString('pt-BR')}
            {attempt.expired && ' · encerrada por tempo esgotado (valeram as respostas salvas até o limite)'}
          </div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 32, fontWeight: 800, lineHeight: 1 }}>{fmtScore(attempt.score)}</div>
          <div style={{ fontSize: 12 }}>mínima {attempt.passing_score}</div>
        </div>
      </div>

      {!attempt.feedback && (
        <div className="training-hint">O gabarito desta avaliação não é exibido após o envio.</div>
      )}

      {attempt.feedback && (
        <div>
          <div className="form-section-title">Gabarito</div>
          {attempt.feedback.map((q, i) => {
            const selected = new Set((q.selected || []).map(String));
            return (
              <div key={q.question_id} className={`training-question ${q.correct ? 'is-correct' : 'is-wrong'}`}>
                <div className="training-question-title">
                  {q.correct ? <LuCheckCircle2 color="var(--success)" /> : <LuXCircle color="var(--danger)" />}
                  <span>{i + 1}. {q.statement}</span>
                </div>
                <div className="training-hint" style={{ marginTop: 0, marginBottom: 8 }}>{QUESTION_TYPES[q.type]} · peso {q.weight}</div>
                {q.options.map((o) => {
                  const mine = selected.has(String(o.id));
                  let cls = '';
                  if (o.is_correct) cls = 'is-right';
                  else if (mine) cls = 'is-wrong';
                  return (
                    <div key={o.id} className={`training-answer ${cls}`}>
                      <span className="training-answer-mark">{mine ? '●' : '○'}</span>
                      <span style={{ flex: 1 }}>{o.text}</span>
                      {o.is_correct && <span className="training-answer-tag">correta</span>}
                      {mine && !o.is_correct && <span className="training-answer-tag">sua resposta</span>}
                    </div>
                  );
                })}
                {q.explanation && <div className="training-explanation"><strong>Explicação:</strong> {q.explanation}</div>}
              </div>
            );
          })}
        </div>
      )}

      <button type="button" className="training-btn training-btn--primary" onClick={onClose} style={{ marginTop: 10 }}>
        <LuArrowLeft /> Voltar ao curso
      </button>
    </div>
  );
}

export default function TrainingAttemptView({ attemptId, onClose }) {
  const [attempt, setAttempt] = useState(null);
  const [answers, setAnswers] = useState({});
  const [saveState, setSaveState] = useState('saved'); // saved | pending | saving | error
  const [secondsLeft, setSecondsLeft] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const deadlineRef = useRef(null);
  const saveTimer = useRef(null);
  const answersRef = useRef({});
  const submittedRef = useRef(false);

  const applyAttempt = useCallback((data) => {
    setAttempt(data);
    if (data.status === 'in_progress') {
      setAnswers(data.answers || {});
      answersRef.current = data.answers || {};
      // Prazo local derivado do "segundos restantes" do servidor (imune a relógio errado no aparelho).
      deadlineRef.current = data.seconds_left !== null ? Date.now() + data.seconds_left * 1000 : null;
      setSecondsLeft(data.seconds_left);
    }
  }, []);

  const reload = useCallback(async () => {
    try {
      const { data } = await api.get(`/my-training/attempts/${attemptId}`);
      applyAttempt(data);
    } catch (e) {
      setError(errorMessage(e, 'Erro ao carregar a avaliação'));
    }
  }, [attemptId, applyAttempt]);

  useEffect(() => { reload(); }, [reload]);

  const submit = useCallback(async (auto = false) => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    clearTimeout(saveTimer.current);
    setSubmitting(true); setError('');
    try {
      const { data } = await api.post(`/my-training/attempts/${attemptId}/submit`, { answers: answersRef.current });
      setAttempt(data);
    } catch (e) {
      submittedRef.current = false;
      if (e.response?.status === 409) { reload(); return; } // já encerrada (ex.: prazo) — mostra o resultado
      setError(errorMessage(e, auto ? 'O tempo acabou, mas não foi possível enviar. Verifique a conexão e tente de novo.' : 'Não foi possível enviar'));
    } finally {
      setSubmitting(false);
    }
  }, [attemptId, reload]);

  // Cronômetro visual; ao zerar, envia automaticamente.
  useEffect(() => {
    if (!attempt || attempt.status !== 'in_progress' || deadlineRef.current === null) return undefined;
    const tick = () => {
      const left = Math.max(0, Math.round((deadlineRef.current - Date.now()) / 1000));
      setSecondsLeft(left);
      if (left <= 0) submit(true);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [attempt, submit]);

  // Avisa antes de fechar a aba com respostas ainda não salvas.
  useEffect(() => {
    const handler = (e) => { if (saveState === 'pending' || saveState === 'saving') { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [saveState]);

  useEffect(() => () => clearTimeout(saveTimer.current), []);

  const persist = useCallback(async () => {
    setSaveState('saving');
    try {
      await api.put(`/my-training/attempts/${attemptId}/answers`, { answers: answersRef.current });
      setSaveState('saved');
    } catch (e) {
      if (e.response?.status === 409) { reload(); return; } // tempo esgotado no servidor
      setSaveState('error');
    }
  }, [attemptId, reload]);

  const handleAnswer = (question, optionId, checked) => {
    setAnswers((prev) => {
      const current = prev[question.id] || [];
      let next;
      if (question.type === 'multiple') next = checked ? [...new Set([...current, optionId])] : current.filter((id) => id !== optionId);
      else next = [optionId];
      const updated = { ...prev, [question.id]: next };
      answersRef.current = updated;
      return updated;
    });
    setSaveState('pending');
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(persist, AUTOSAVE_DELAY_MS);
  };

  const handleSubmit = () => {
    const unanswered = attempt.assessment.questions.filter((q) => !(answers[q.id] || []).length).length;
    const msg = unanswered
      ? `Você deixou ${unanswered} questão(ões) sem resposta. Enviar mesmo assim? Depois do envio não é possível alterar.`
      : 'Enviar suas respostas? Depois do envio não é possível alterar.';
    if (window.confirm(msg)) submit(false);
  };

  const handleLeave = async () => {
    if (saveState === 'pending') { clearTimeout(saveTimer.current); await persist(); }
    onClose();
  };

  if (!attempt) {
    return (
      <div className="page-card">
        <button type="button" className="training-btn" onClick={onClose}><LuArrowLeft /> Voltar</button>
        <div className="training-hint" style={{ marginTop: 16 }}>{error || 'Carregando…'}</div>
      </div>
    );
  }

  if (attempt.status !== 'in_progress') return <ResultView attempt={attempt} onClose={onClose} />;

  const questions = attempt.assessment.questions;
  const answeredCount = questions.filter((q) => (answers[q.id] || []).length).length;
  const lowTime = secondsLeft !== null && secondsLeft <= 60;

  const saveLabel = {
    saved: <><LuCloud /> Respostas salvas</>,
    pending: <><LuUploadCloud /> Salvando…</>,
    saving: <><LuUploadCloud /> Salvando…</>,
    error: <><LuAlertTriangle /> Falha ao salvar — verifique a conexão</>,
  }[saveState];

  return (
    <div className="page-card">
      <div className="training-attempt-bar">
        <button type="button" className="training-btn training-btn--sm" onClick={handleLeave}><LuArrowLeft /> Sair (continua depois)</button>
        <div className="training-attempt-status">
          <span className={`training-save-state ${saveState === 'error' ? 'is-error' : ''}`}>{saveLabel}</span>
          <span>{answeredCount}/{questions.length} respondidas</span>
          {secondsLeft !== null && (
            <span className={`training-timer ${lowTime ? 'is-low' : ''}`}><LuClock /> {fmtClock(secondsLeft)}</span>
          )}
        </div>
      </div>

      <div className="training-player-heading">
        <h2>{attempt.assessment.title}</h2>
        <div className="training-row-meta">
          <span>Tentativa {attempt.attempt_number}</span>
          <span>Nota mínima {attempt.assessment.passing_score}</span>
          {attempt.assessment.time_limit_minutes && <span>Tempo limite {attempt.assessment.time_limit_minutes} min</span>}
        </div>
      </div>

      {error && <div className="alert-custom danger">{error}</div>}

      {questions.map((q, i) => {
        const selected = answers[q.id] || [];
        return (
          <div key={q.id} className="training-question">
            <div className="training-question-title"><span>{i + 1}. {q.statement}</span></div>
            <div className="training-hint" style={{ marginTop: 0, marginBottom: 8 }}>
              {q.type === 'multiple' ? 'Marque todas as alternativas corretas' : QUESTION_TYPES[q.type]} · peso {q.weight}
            </div>
            {q.options.map((o) => (
              <label key={o.id} className={`training-answer is-selectable ${selected.includes(o.id) ? 'is-selected' : ''}`}>
                <input
                  type={q.type === 'multiple' ? 'checkbox' : 'radio'}
                  name={`q-${q.id}`}
                  checked={selected.includes(o.id)}
                  onChange={(e) => handleAnswer(q, o.id, e.target.checked)}
                />
                <span style={{ flex: 1 }}>{o.text}</span>
              </label>
            ))}
          </div>
        );
      })}

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap', marginTop: 10 }}>
        <button type="button" className="training-btn" onClick={handleLeave}>Salvar e sair</button>
        <button type="button" className="training-btn training-btn--primary" onClick={handleSubmit} disabled={submitting}>
          <LuSend /> {submitting ? 'Enviando…' : 'Enviar respostas'}
        </button>
      </div>
    </div>
  );
}
